import { supabase } from './supabase/client';

/**
 * Clear all authentication data
 */
export async function clearAuthData() {
  try {
    // Sign out from Supabase (this clears the refresh token)
    await supabase.auth.signOut();
  } catch (error) {
  }
  
  // Clear localStorage
  localStorage.removeItem('accessToken');
  localStorage.removeItem('userId');
  localStorage.removeItem('userProfile');
  localStorage.removeItem('userProfileTimestamp');
  
  // Clear sessionStorage
  sessionStorage.clear();
  
}

/**
 * Get a valid access token, refreshing if necessary
 */
export async function getValidAccessToken(): Promise<string | null> {
  const startTime = Date.now();
  try {
    
    // First, check if we have a token in localStorage
    const cachedToken = localStorage.getItem('accessToken');
    
    // If no cached token, check Supabase but don't wait too long
    if (!cachedToken) {
      
      try {
        // Quick check with 2-second timeout
        const timeoutPromise = new Promise<null>((_, reject) => 
          setTimeout(() => reject(new Error('Timeout')), 2000)
        );
        
        const sessionPromise = supabase.auth.getSession();
        
        const result = await Promise.race([sessionPromise, timeoutPromise]);
        
        if (result && 'data' in result) {
          const { data: { session }, error } = result as any;
          const elapsed = Date.now() - startTime;
          
          if (session && session.access_token && !error) {
            localStorage.setItem('accessToken', session.access_token);
            return session.access_token;
          }
          
          if (error && (error.message.includes('refresh') || error.message.includes('Refresh Token') || error.message.includes('Invalid Refresh Token'))) {
            await clearAuthData();
            return null;
          }
        }
      } catch (supabaseError: any) {
        const elapsed = Date.now() - startTime;
      }
      
      return null;
    }
    
    // We have a cached token, use it directly for speed
    const elapsed = Date.now() - startTime;
    return cachedToken;
    
  } catch (error: any) {
    const elapsed = Date.now() - startTime;
    
    // Last resort: try cached token
    const cachedToken = localStorage.getItem('accessToken');
    if (cachedToken) {
      return cachedToken;
    }
    
    return null;
  }
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  const token = await getValidAccessToken();
  return token !== null;
}

/**
 * Logout user
 */
export async function logout() {
  await clearAuthData();
  
  // Dispatch logout event so other components can clean up
  window.dispatchEvent(new Event('userLoggedOut'));
  
}
